import { z } from 'zod';

const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/u);
const year = z.number().int().min(2000).max(2200);
const date = z.iso.date();
const text = (max: number) => z.string().trim().max(max);
export const hqMonthlyMeetingAttachmentSchema = z.object({
  manifestId: id, sha256, kind: z.enum(['photo', 'signature', 'document']),
  fileName: text(240).min(1), mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']),
  byteSize: z.number().int().positive().max(50 * 1024 * 1024),
}).strict().superRefine((value, context) => {
  if (value.kind === 'photo' ? !value.mimeType.startsWith('image/') : value.mimeType !== 'application/pdf')
    context.addIssue({ code: 'custom', message: 'Photos require an image; signature sheets and documents require PDF' });
});
export type HqMonthlyMeetingAttachment = z.infer<typeof hqMonthlyMeetingAttachmentSchema>;
export const hqMonthlyMeetingDraftSchema = z.object({
  year, month: z.number().int().min(1).max(12), round: z.number().int().positive().max(9999).nullable(),
  title: text(300).min(1), meetingDate: date.nullable(),
  startsAt: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u).nullable(),
  location: text(500), facilitator: text(200),
  attendees: z.array(z.object({ id, name: text(200).min(1), organization: text(300), position: text(200) }).strict()).max(300),
  agenda: z.array(z.object({ id, title: text(500).min(1), notes: text(5000), decision: text(5000), owner: text(300), dueDate: date.nullable(), siteId: id.nullable() }).strict()).max(100),
  minutes: text(50000), attachments: z.array(hqMonthlyMeetingAttachmentSchema).max(30),
  historicalReferenceId: id.nullable(), status: z.enum(['draft', 'completed', 'cancelled']),
}).strict().superRefine((draft, context) => {
  if (draft.meetingDate && (Number(draft.meetingDate.slice(0, 4)) !== draft.year || Number(draft.meetingDate.slice(5, 7)) !== draft.month))
    context.addIssue({ code: 'custom', path: ['meetingDate'], message: 'Meeting date must belong to the selected month' });
  for (const key of ['attendees', 'agenda', 'attachments'] as const) {
    const values = draft[key].map(value => 'id' in value ? value.id : value.manifestId);
    if (new Set(values).size !== values.length) context.addIssue({ code: 'custom', path: [key], message: 'Duplicate entry' });
  }
  if (draft.status === 'completed' && (!draft.meetingDate || !draft.minutes))
    context.addIssue({ code: 'custom', message: 'A completed meeting requires its actual date and minutes' });
});
export type HqMonthlyMeetingDraft = z.infer<typeof hqMonthlyMeetingDraftSchema>;
const site = z.object({ id, name: text(200).min(1) }).strict();
export const hqMonthlyMeetingDocumentSchema = z.object({
  schemaVersion: z.literal(1), tenantId: z.literal('kop'), scope: z.literal('headquarters'),
  meetingId: id, year, yearKey: z.string().regex(/^\d{4}$/u), version: z.number().int().positive(), draft: hqMonthlyMeetingDraftSchema,
  sites: z.array(site).max(200), historicalReferenceHash: sha256.nullable(),
  savedAt: z.iso.datetime(), savedBy: id, contentHash: sha256,
}).strict();
export type HqMonthlyMeetingDocument = z.infer<typeof hqMonthlyMeetingDocumentSchema>;
export const hqMonthlyMeetingSummarySchema = z.object({
  meetingId: id, year, month: z.number().int().min(1).max(12), title: text(300).min(1),
  meetingDate: date.nullable(), round: z.number().int().positive().nullable(),
  version: z.number().int().positive(), savedAt: z.iso.datetime(), status: z.enum(['draft', 'completed', 'cancelled']),
  attachmentCount: z.number().int().nonnegative(), attendeeCount: z.number().int().nonnegative(), historicalReferenceId: id.nullable(),
  coverAttachment: hqMonthlyMeetingAttachmentSchema.nullable(),
}).strict();
export const hqMonthlyMeetingsLoadInputSchema = z.object({ year, meetingId: id.optional() }).strict();
export const hqMonthlyMeetingsLoadResultSchema = z.object({
  records: z.array(hqMonthlyMeetingSummarySchema).max(120), document: hqMonthlyMeetingDocumentSchema.nullable(),
  sites: z.array(site).max(200), canEdit: z.boolean(),
}).strict();
export type HqMonthlyMeetingsLoadResult = z.infer<typeof hqMonthlyMeetingsLoadResultSchema>;
export const hqMonthlyMeetingSaveInputSchema = z.object({ commandId: id, meetingId: id, expectedVersion: z.number().int().nonnegative(), draft: hqMonthlyMeetingDraftSchema }).strict();
export type HqMonthlyMeetingSaveInput = z.infer<typeof hqMonthlyMeetingSaveInputSchema>;
export const hqMonthlyMeetingSaveResultSchema = z.object({
  document: hqMonthlyMeetingDocumentSchema,
  receipt: z.object({ commandId: id, version: z.number().int().positive(), savedAt: z.iso.datetime(), replayed: z.boolean() }).strict(),
}).strict();
export type HqMonthlyMeetingSaveResult = z.infer<typeof hqMonthlyMeetingSaveResultSchema>;
export const hqMonthlyMeetingEvidencePrepareSchema = z.object({
  meetingId: id, kind: z.enum(['photo', 'signature', 'document']), fileName: text(240).min(1),
  mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']), byteSize: z.number().int().positive().max(50 * 1024 * 1024), sha256, origin: z.url().max(300),
}).strict().superRefine((value, context) => {
  if (value.kind === 'photo' ? !value.mimeType.startsWith('image/') : value.mimeType !== 'application/pdf')
    context.addIssue({ code: 'custom', message: 'Attachment kind and file type differ' });
});
export const hqMonthlyMeetingEvidenceKeySchema = z.object({ meetingId: id, manifestId: id }).strict();
export const hqMonthlyMeetingEvidenceOpenSchema = hqMonthlyMeetingEvidenceKeySchema.extend({ disposition: z.enum(['inline', 'attachment']) }).strict();
export const hqMonthlyMeetingEvidencePreparedSchema = z.object({ manifestId: id, uploadUrl: z.url(), expiresAt: z.iso.datetime(), method: z.literal('PUT'), headers: z.object({ 'Content-Type': z.string() }).strict() }).strict();
export const hqMonthlyMeetingEvidenceOpenedSchema = z.object({ url: z.url(), expiresAt: z.iso.datetime(), fileName: z.string(), mimeType: z.string() }).strict();
export const hqMonthlyMeetingManifestSchema = hqMonthlyMeetingAttachmentSchema.safeExtend({
  tenantId: z.literal('kop'), scope: z.literal('headquarters'), meetingId: id, ownerUid: id,
  objectPath: z.string(), status: z.enum(['pending', 'committed']), createdAt: z.iso.datetime(), expiresAt: z.iso.datetime(),
  generation: z.string().nullable(), committedAt: z.iso.datetime().nullable(),
}).strict();
export type HqMonthlyMeetingManifest = z.infer<typeof hqMonthlyMeetingManifestSchema>;
